import React, { useState, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { supabase } from './supabaseClient';
import { SET_REGISTRY } from './setRegistry';
import { getParallelsForSet } from './sets/parallelsConfig';
import { isScannerHostEnabled } from './scannerAccess';
import { decodeImage, resizeImage } from './CardImage';
import CollectionModal from './CollectionModal';

// AI Card Scanner — Scanner v3.0 Phase 1 (staging only).
// Photo -> scan-card edge function -> database-reconciled identity -> one tap to
// the existing add-to-collection modal. Raw cards only; graded routes to manual.

const COLORS = {
  gold: 'var(--gold)', cyan: 'var(--cyan)', green: 'var(--green)', amber: 'var(--warn)',
  red: 'var(--red-2)', bg: 'var(--bg)', cardBg: 'var(--panel)', muted: 'var(--muted-2)',
};

// Briefing spec: max 1600px, JPEG 0.85 — under the vision 5MB + edge body limits.
// Decodes via createImageBitmap (see CardImage.decodeImage) so full-res Android
// camera JPEGs, which can exceed the <img> decode limit, scan reliably.
async function compressToBase64(file, maxDim = 1600, quality = 0.85) {
  const src = await decodeImage(file);
  const canvas = document.createElement('canvas');
  let width = src.width, height = src.height;
  if (width > maxDim || height > maxDim) {
    if (width > height) { height = Math.round((height * maxDim) / width); width = maxDim; }
    else { width = Math.round((width * maxDim) / height); height = maxDim; }
  }
  canvas.width = width; canvas.height = height;
  canvas.getContext('2d').drawImage(src, 0, 0, width, height);
  if (typeof src.close === 'function') src.close(); // release ImageBitmap memory
  return canvas.toDataURL('image/jpeg', quality).split(',')[1];
}

// turn the compressed JPEG base64 (already ~1600px) into a File for upload
function base64ToJpegFile(b64, name) {
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new File([arr], name, { type: 'image/jpeg' });
}

// loose number match: "05" === "5", "#5" === "5", "A-HF" === "a-hf"
const normNum = (x) => String(x || '').trim().toUpperCase().replace(/[^A-Z0-9-]/g, '').replace(/^0+(?=\d)/, '');
// sub-set title match: compare the part before an em-dash, alphanumerics only
// Normalize a sub-set title for JS-title <-> DB-name matching. Strip a trailing
// "— N CARDS" count (JS titles carry it; DB names don't) but KEEP the full
// subtitle — truncating at the first em-dash collapsed distinct sub-sets that
// share a prefix (e.g. "THE GALACTIC CIVIL WAR — REBEL ALLIANCE" vs "— GALACTIC
// EMPIRE"), making the wrong sub-set resolve and dropping the parallel.
const tnorm = (s) => String(s || '').toUpperCase()
  .replace(/[^A-Z0-9 ]/g, ' ').replace(/\s+/g, ' ').replace(/ \d+ CARDS?$/, '').trim();

const BASE_OPT = 'base'; // dropdown value meaning "no parallel"

const PROV = {
  verified: { c: COLORS.green, t: 'VERIFIED' },
  unverified: { c: COLORS.amber, t: 'UNVERIFIED' },
  empty: { c: COLORS.muted, t: 'NONE' },
};
function Pill({ state }) {
  const m = PROV[state] || PROV.empty;
  return (
    <span style={{
      fontFamily: 'Share Tech Mono, monospace', fontSize: 9, letterSpacing: 0.5,
      padding: '2px 7px', borderRadius: 3, border: `1px solid ${m.c}`, color: m.c, whiteSpace: 'nowrap',
    }}>{m.t}</span>
  );
}
function Field({ label, value, state }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: `1px solid ${COLORS.muted}22` }}>
      <div style={{ color: COLORS.cyan, fontSize: 11, width: 88, letterSpacing: 0.5, flexShrink: 0 }}>{label}</div>
      <div style={{ color: '#fff', fontSize: 13, flex: 1, wordBreak: 'break-word' }}>
        {value === null || value === undefined || value === '' ? '—' : String(value)}
      </div>
      {state && <Pill state={state} />}
    </div>
  );
}

// map a DB-resolved card to the frontend set file objects the modal needs
async function resolveFrontend(activeCard) {
  const setMeta = SET_REGISTRY.find((s) => s.id === activeCard.set_slug);
  if (!setMeta) return null;
  const mod = await import(`./sets/${setMeta.dataFile}.js`);
  const SETS = mod.SETS || [];
  const ftype = { base: 'base', insert: 'insert', autograph: 'auto' }[activeCard.sub_set_type] || activeCard.sub_set_type;
  const ofType = SETS.filter((s) => s.type === ftype);
  const set = ofType.find((s) => tnorm(s.title) === tnorm(activeCard.sub_set_name))
    || ofType.find((s) => tnorm(s.title).includes(tnorm(activeCard.sub_set_name)) || tnorm(activeCard.sub_set_name).includes(tnorm(s.title)))
    || (ofType.length === 1 ? ofType[0] : null);
  if (!set) return null;
  const card = set.cards.find((c) => normNum(c.n) === normNum(activeCard.card_number));
  if (!card) return null;
  return { setMeta, set, card };
}

// Scan progress meter. The server answers in one shot, so the steps are paced
// on a clock tuned to the typical scan time: the first five tick on schedule
// and the last keeps spinning until the real result lands (done), then all go
// green. A faster scan simply finishes the meter sooner.
const SCAN_STEPS = [
  'Verifying year...',
  'Verifying set...',
  'Verifying card number...',
  'Verifying card name...',
  'Verifying card parallel...',
  'Verifying serial number...',
];
// Tuned to staging timing logs (2026-10-25): the server takes 9.5-13.3s, plus
// photo prep + upload on the phone, so the meter is spread across ~12s.
const SCAN_STEP_AT_MS = [1300, 3100, 5000, 7000, 9200]; // when steps 1-5 tick
const SCAN_TYPICAL_MS = 12000;

function ScanProgress({ done }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const t0 = Date.now();
    const id = setInterval(() => setElapsed(Date.now() - t0), 100);
    return () => clearInterval(id);
  }, []);
  const ticked = done ? SCAN_STEPS.length : SCAN_STEP_AT_MS.filter((t) => elapsed >= t).length;
  // Eases toward 92% and only fills the rest once the result is in.
  const pct = done ? 100 : 92 * (1 - Math.exp(-elapsed / (SCAN_TYPICAL_MS / 2.6)));
  return (
    <div role="status" aria-live="polite" style={{ marginTop: 16, padding: '12px 14px', background: COLORS.cardBg, borderRadius: 6 }}>
      <style>{'@keyframes ckScanSpin { to { transform: rotate(360deg); } }'}</style>
      <div style={{ height: 4, background: 'var(--panel-3)', borderRadius: 2, overflow: 'hidden', marginBottom: 10 }}>
        <div style={{ height: '100%', width: `${pct}%`, background: COLORS.green, transition: 'width 0.3s linear' }} />
      </div>
      {SCAN_STEPS.map((label, i) => {
        const ok = i < ticked;
        const on = i === ticked;
        return (
          <div key={label} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0',
            fontFamily: 'Share Tech Mono, monospace', fontSize: 13,
            color: ok ? COLORS.green : (on ? '#fff' : COLORS.muted), transition: 'color 0.25s',
          }}>
            <span aria-hidden="true" style={{
              width: 18, height: 18, borderRadius: '50%', flexShrink: 0, boxSizing: 'border-box',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700,
              border: `1.5px solid ${ok ? COLORS.green : (on ? COLORS.gold : 'var(--panel-3)')}`,
              borderTopColor: on ? 'transparent' : undefined,
              background: ok ? COLORS.green : 'transparent', color: COLORS.bg,
              animation: on ? 'ckScanSpin 0.8s linear infinite' : 'none',
            }}>{ok ? '✓' : ''}</span>
            {label}
          </div>
        );
      })}
    </div>
  );
}

export default function AiCardScanner({ user }) {
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [frontPreview, setFrontPreview] = useState(null);
  const [backPreview, setBackPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [progress, setProgress] = useState(null); // null | 'running' | 'done'
  const [result, setResult] = useState(null);

  // collector choices on top of the scan result
  const [chosenCard, setChosenCard] = useState(null);     // a candidate (collision pick)
  const [chosenParallelId, setChosenParallelId] = useState(BASE_OPT); // dropdown selection
  const [frontendParallels, setFrontendParallels] = useState([]); // constrained dropdown options
  const [mapped, setMapped] = useState(null);             // { setMeta, set, card } for the active card
  const [scanPhotos, setScanPhotos] = useState(null);     // { front: File, back: File }
  const [modal, setModal] = useState(null);

  const pickerRef = useRef(null);
  const [picking, setPicking] = useState(null);
  const [eff, setEff] = useState(null); // { green_count, considered_count, pct } — global gauge

  // Load the global green-result rate once on open. Non-essential: if it fails
  // the gauge just doesn't show — never block the scanner.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.rpc('scan_efficiency');
        if (!cancelled && !error && data && data.length) setEff(data[0]);
      } catch (_e) { /* gauge is optional */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const activeCard = chosenCard || result?.card || null;

  // When the resolved/chosen card changes, load its constrained parallel list
  // (same call the add-modal uses) and pre-select the scan's resolved parallel.
  useEffect(() => {
    let cancelled = false;
    if (!activeCard) { setFrontendParallels([]); setMapped(null); setChosenParallelId(BASE_OPT); return; }
    (async () => {
      let m = null;
      try { m = await resolveFrontend(activeCard); } catch (_e) { m = null; }
      if (cancelled) return;
      if (!m) { setFrontendParallels([]); setMapped(null); setChosenParallelId(BASE_OPT); return; }
      const list = getParallelsForSet(m.setMeta.id, m.set.id, m.set) || [];
      setFrontendParallels(list);
      setMapped(m);
      // Note: a resolved NUMBERED-BASE SLOT has name '' (falsy) — test for the
      // parallel object itself, or a server-resolved slot silently drops to
      // "Base — no parallel".
      const rname = result?.parallel ? String(result.parallel.name || '') : null;
      if (rname != null) {
        const hit = list.find((p) => (p.name || '').toUpperCase() === rname.toUpperCase());
        setChosenParallelId(hit ? hit.id : BASE_OPT);
      } else if (result?.parallel_provenance === 'unverified' && (result?.parallel_candidates || []).length > 0) {
        setChosenParallelId(''); // genuine collision (e.g. /199) — force a pick
      } else {
        // No server-resolved parallel — typically a same-year sub-set collision the
        // collector just picked, so the server never ran serial→parallel for the
        // chosen sub-set. Narrow it here from the read serial: a denominator hitting
        // exactly one print run pre-selects it; one hitting several (e.g. two /1s)
        // forces a pick; none falls back to base. A serial-numbered card must never
        // silently default to "no parallel".
        const den = result?.ai?.serial_denominator;
        const sameRun = den != null ? list.filter((p) => p.print_run === den) : [];
        if (sameRun.length === 1) setChosenParallelId(sameRun[0].id);
        else if (sameRun.length > 1) setChosenParallelId('');
        else setChosenParallelId(BASE_OPT);
      }
    })();
    return () => { cancelled = true; };
  }, [activeCard, result]);

  const acceptFile = async (side, file) => {
    if (side === 'front') setFrontFile(file); else setBackFile(file);
    // Downscaled preview, not the raw file: full-res camera JPEGs can exceed the
    // <img> decode limit and render blank on Android. Fall back to the raw file.
    let url;
    try {
      url = URL.createObjectURL(await resizeImage(file, 1600, 1600));
    } catch (_e) {
      url = URL.createObjectURL(file);
    }
    if (side === 'front') setFrontPreview(url); else setBackPreview(url);
  };
  const openPicker = async (side) => {
    // Native shell: the webview's file input can't open the camera directly, so
    // use the real camera via @capacitor/camera. The action sheet still offers
    // the photo library for cards photographed earlier. Downstream is unchanged
    // — the plugin result is converted to a File like the web input produces.
    if (Capacitor.isNativePlatform()) {
      try {
        const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
        const photo = await Camera.getPhoto({
          resultType: CameraResultType.Uri,
          source: CameraSource.Prompt,
          quality: 90,
          correctOrientation: true,
          promptLabelHeader: side === 'front' ? 'Card front' : 'Card back',
          promptLabelPicture: 'Take Photo',
          promptLabelPhoto: 'From Photos',
        });
        const blob = await (await fetch(photo.webPath)).blob();
        const file = new File([blob], `scan-${side}.${photo.format || 'jpeg'}`, { type: blob.type || 'image/jpeg' });
        await acceptFile(side, file);
      } catch (e) {
        // Cancelling the sheet rejects too — only surface real failures.
        const msg = String(e?.message || '');
        if (!/cancel/i.test(msg)) setStatus('Camera unavailable — check the camera permission in Settings.');
      }
      return;
    }
    setPicking(side);
    if (pickerRef.current) { pickerRef.current.value = ''; pickerRef.current.click(); }
  };
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const side = picking;
    setPicking(null);
    await acceptFile(side, file);
  };
  const clearAll = () => {
    setFrontFile(null); setBackFile(null); setFrontPreview(null); setBackPreview(null);
    setResult(null); setStatus(''); setChosenCard(null); setChosenParallelId(BASE_OPT);
    setScanPhotos(null); setFrontendParallels([]); setMapped(null);
  };

  const scan = async () => {
    if (!frontFile || !backFile) { setStatus('Please add both the front and back photo.'); return; }
    setBusy(true); setResult(null); setChosenCard(null); setStatus('');
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session) { setStatus('Please sign in first.'); setBusy(false); return; }
      setProgress('running');
      const [front_base64, back_base64] = await Promise.all([compressToBase64(frontFile), compressToBase64(backFile)]);
      setScanPhotos({
        front: base64ToJpegFile(front_base64, 'scan-front.jpg'),
        back: base64ToJpegFile(back_base64, 'scan-back.jpg'),
      });
      const { data, error } = await supabase.functions.invoke('scan-card', { body: { front_base64, back_base64 } });
      if (error) { setStatus(`Scan error: ${error.message}`); setResult(null); }
      else if (data && data.success === false) { setStatus(`Scan error: ${data.error || 'unknown'}`); setResult(null); }
      else {
        // Let the collector see every step go green before the result replaces it.
        setProgress('done');
        await new Promise((r) => setTimeout(r, 500));
        setResult(data); setStatus('');
      }
    } catch (err) {
      setStatus(`Error: ${err.message}`);
    } finally {
      setProgress(null);
      setBusy(false);
    }
  };

  const selectedParallel = frontendParallels.find((p) => p.id === chosenParallelId) || null;
  const resolvedDbId = (() => {
    if (result?.parallel_provenance !== 'verified' || !result?.parallel) return null;
    const hit = frontendParallels.find((p) => (p.name || '').toUpperCase() === result.parallel.name.toUpperCase());
    return hit ? hit.id : null;
  })();
  const parallelPill = (chosenParallelId === BASE_OPT || chosenParallelId === '')
    ? 'empty'
    : (chosenParallelId === resolvedDbId ? 'verified' : 'unverified');
  const parallelUnconfirmed = chosenParallelId === '';
  // Unresolved parallel: the server's one-tap candidates (e.g. the three 2026
  // Chrome /25s) go first in the dropdown. They only apply to the server's own
  // card, never a collector-picked collision candidate.
  const candKeys = new Set((result?.parallel_candidates || [])
    .map((c) => `${(c.name || '').toUpperCase()}|${c.print_run ?? ''}`));
  const matchParallels = (!chosenCard && result?.parallel_provenance !== 'verified')
    ? frontendParallels.filter((p) => candKeys.has(`${(p.name || '').toUpperCase()}|${p.print_run ?? ''}`))
    : [];
  const otherParallels = frontendParallels.filter((p) => !matchParallels.includes(p));
  const sharedRun = matchParallels.length > 1 && matchParallels.every((p) => p.print_run === matchParallels[0].print_run)
    ? matchParallels[0].print_run : null;

  async function captureCorrection(finalCard, finalParallelName) {
    try {
      const ai = result?.ai || {};
      const changed = [];
      if (result?.card && finalCard && result.card.card_id !== finalCard.card_id) changed.push('card_choice');
      if (!result?.card && finalCard) changed.push('card_choice');
      const recParallel = result?.parallel?.name || null;
      if ((finalParallelName || null) !== recParallel) changed.push('parallel');
      await supabase.from('ai_scan_corrections').insert({
        user_id: user?.id,
        card_key: 'scan-card-confirm',
        set_id: finalCard?.set_slug || null,
        sub_set_type: finalCard?.sub_set_type || null,
        card_number: ai.card_number || null,
        ai_result: ai,
        user_result: {
          set_slug: finalCard?.set_slug || null,
          card_number: finalCard?.card_number || null,
          name: finalCard?.name || null,
          parallel_name: finalParallelName || null,
        },
        changed_fields: changed,
        was_corrected: changed.length > 0,
        scan_context: { feature: 'scan-card-ui', decision: result?.decision || null, reason: result?.reason || null },
      });
    } catch (_e) { /* never block the collector */ }
  }

  async function addToChecklist() {
    if (!activeCard) return;
    if (parallelUnconfirmed) { setStatus('Choose the parallel above first.'); return; }
    setStatus('Opening...');
    try {
      const m = mapped || await resolveFrontend(activeCard);
      if (!m) { setStatus('Could not match this card to the checklist — open the set to add it by hand.'); return; }
      const parallelId = chosenParallelId === '' ? BASE_OPT : chosenParallelId;
      const finalParallelName = selectedParallel?.name || null;
      captureCorrection(activeCard, finalParallelName);
      setStatus('');
      setModal({ ...m, parallelId });
    } catch (err) {
      setStatus(`Could not open the add screen: ${err.message}`);
    }
  }

  if (!isScannerHostEnabled()) {
    return (
      <div style={{ minHeight: '100vh', background: COLORS.bg, padding: 24, fontFamily: 'Rajdhani, sans-serif', color: '#fff' }}>
        <div style={{ maxWidth: 560, margin: '40px auto' }}>
          <div style={{ fontFamily: 'Orbitron, sans-serif', color: COLORS.gold, fontSize: 20, letterSpacing: 1, marginBottom: 8 }}>AI CARD SCANNER</div>
          <p style={{ color: COLORS.muted, fontSize: 14, lineHeight: 1.5 }}>The card scanner is in testing and not available here yet. Check back soon.</p>
        </div>
      </div>
    );
  }

  const dec = result?.decision;
  const ai = result?.ai || {};

  const btn = (active) => ({
    width: '100%', padding: 10, background: active ? COLORS.cardBg : 'transparent', color: COLORS.cyan,
    border: `1px solid ${COLORS.cyan}`, borderRadius: 4, fontFamily: 'Orbitron, sans-serif', fontSize: 11,
    letterSpacing: 1, cursor: busy ? 'not-allowed' : 'pointer',
  });
  const primary = (color, enabled = true) => ({
    width: '100%', padding: 13, background: enabled ? color : COLORS.muted, color: COLORS.bg, border: 'none',
    borderRadius: 4, fontFamily: 'Orbitron, sans-serif', fontSize: 13, letterSpacing: 2, fontWeight: 700,
    cursor: enabled && !busy ? 'pointer' : 'not-allowed', marginTop: 10,
  });

  return (
    <div style={{ minHeight: '100vh', background: COLORS.bg, padding: 16, fontFamily: 'Rajdhani, sans-serif', color: '#fff' }}>
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <button
          onClick={() => {
            const from = new URLSearchParams(window.location.search).get('from');
            window.history.pushState({}, '', from ? `/sets/${from}` : '/');
            window.dispatchEvent(new PopStateEvent('popstate'));
          }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Orbitron, sans-serif', fontSize: 10, letterSpacing: 2, color: COLORS.muted, padding: '0 0 12px', display: 'block' }}
        >← BACK TO CHECKLIST</button>
        <div style={{ fontFamily: 'Orbitron, sans-serif', color: COLORS.gold, fontSize: 20, letterSpacing: 1, marginBottom: 4 }}>AI CARD SCANNER</div>
        <div style={{ fontFamily: 'Share Tech Mono, monospace', color: COLORS.green, fontSize: 11, letterSpacing: 2, marginBottom: 20 }}>SUPPORTED: GALAXY 2018 / 2021 / 2022 / 2023 / 2024 / 2025 / 2026 · CHROME 2024 / 2025 / 2026 · CHROME BLACK 2024 · MASTERWORK 2025 · SMUGGLER'S OUTPOST 2025 · HYPERSPACE 2025</div>

        {eff && (() => {
          const pct = Number(eff.pct) || 0;
          const C = 2 * Math.PI * 44;
          const off = C * (1 - pct / 100);
          return (
            <div style={{ background: 'var(--panel)', border: '0.5px solid var(--panel-3)', borderRadius: 10, padding: 16, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
              <svg width="96" height="96" viewBox="0 0 104 104" style={{ flexShrink: 0 }} role="img" aria-label={`Green-result rate: ${pct} percent`}>
                <circle cx="52" cy="52" r="44" fill="none" stroke="var(--panel-3)" strokeWidth="9" />
                <circle cx="52" cy="52" r="44" fill="none" stroke={COLORS.green} strokeWidth="9" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={off} transform="rotate(-90 52 52)" />
                <text x="52" y="50" textAnchor="middle" dominantBaseline="middle" style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, fontSize: 26, fill: '#fff' }}>{pct}%</text>
                <text x="52" y="69" textAnchor="middle" dominantBaseline="middle" style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: 8, letterSpacing: 1, fill: 'var(--ok)' }}>GREEN</text>
              </svg>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'Orbitron, sans-serif', fontWeight: 700, letterSpacing: 1, color: COLORS.green, fontSize: 12, marginBottom: 6 }}>GREEN-RESULT RATE</div>
                <div style={{ color: 'var(--text)', fontSize: 12, lineHeight: 1.5 }}>Share of in-scope scans matched with full confidence, no confirm needed.</div>
                <div style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: 10, color: COLORS.muted, marginTop: 8 }}>{eff.green_count} of {eff.considered_count} scans · all collectors</div>
              </div>
            </div>
          );
        })()}

        <p style={{ color: COLORS.muted, fontSize: 14, marginBottom: 20, lineHeight: 1.5 }}>
          Take a photo of the front and back of a raw card from any supported set, and add it to your checklist with one tap.
        </p>

        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <button onClick={() => openPicker('front')} disabled={busy} style={btn(!!frontPreview)}>{frontPreview ? 'RETAKE FRONT' : 'ADD FRONT PHOTO'}</button>
            {frontPreview && <img src={frontPreview} alt="front" style={{ width: '100%', marginTop: 8, borderRadius: 4 }} />}
          </div>
          <div style={{ flex: 1 }}>
            <button onClick={() => openPicker('back')} disabled={busy} style={btn(!!backPreview)}>{backPreview ? 'RETAKE BACK' : 'ADD BACK PHOTO'}</button>
            {backPreview && <img src={backPreview} alt="back" style={{ width: '100%', marginTop: 8, borderRadius: 4 }} />}
          </div>
        </div>
        <input ref={pickerRef} type="file" accept="image/*" onChange={onPick} style={{ display: 'none' }} />

        <button onClick={scan} disabled={busy || !frontFile || !backFile} style={primary(COLORS.gold, !(busy || !frontFile || !backFile))}>
          {busy ? 'SCANNING...' : 'IDENTIFY CARD'}
        </button>
        <button onClick={clearAll} disabled={busy} style={{ width: '100%', padding: 10, marginTop: 8, background: 'transparent', color: COLORS.muted, border: `1px solid ${COLORS.muted}`, borderRadius: 4, fontFamily: 'Rajdhani, sans-serif', fontSize: 12, letterSpacing: 1, cursor: busy ? 'not-allowed' : 'pointer' }}>CLEAR</button>

        {progress && <ScanProgress done={progress === 'done'} />}

        {status && (
          <div style={{ marginTop: 16, padding: 12, background: COLORS.cardBg, borderRadius: 4, fontFamily: 'Share Tech Mono, monospace', color: COLORS.green, fontSize: 12 }}>{status}</div>
        )}

        {dec === 'graded' && (
          <div style={{ background: COLORS.cardBg, padding: 16, borderRadius: 8, marginTop: 16 }}>
            <div style={{ fontFamily: 'Orbitron, sans-serif', color: COLORS.gold, fontSize: 14, letterSpacing: 1, marginBottom: 10 }}>GRADED CARD</div>
            <p style={{ color: '#fff', fontSize: 14, lineHeight: 1.5 }}>This card looks graded. Graded cards are added by hand for now — open the card in your checklist to enter the cert number and grade.</p>
          </div>
        )}

        {dec === 'no_match' && (() => {
          // If vision confidently read a year that isn't one we cover, the card is
          // almost certainly from another set — say so plainly instead of the vague
          // "try clearer photos". A blurry/unreadable card has no year and falls
          // through to the original message.
          const SUPPORTED_YEARS = [2018, 2021, 2022, 2023, 2024, 2025, 2026];
          const readYear = Number(ai.back_set_year || ai.year) || null;
          const otherSet = readYear && !SUPPORTED_YEARS.includes(readYear);
          return otherSet ? (
            <div style={{ background: COLORS.cardBg, padding: 16, borderRadius: 8, marginTop: 16, borderLeft: `3px solid ${COLORS.amber}` }}>
              <div style={{ fontFamily: 'Orbitron, sans-serif', color: COLORS.amber, fontSize: 14, letterSpacing: 1, marginBottom: 10 }}>DIFFERENT SET</div>
              <p style={{ color: '#fff', fontSize: 14, lineHeight: 1.5 }}>This scanner currently covers Star Wars Galaxy 2018, 2021, 2022, 2023, 2024, 2025, and 2026, plus 2024, 2025, and 2026 Topps Chrome, 2024 Chrome Black, 2025 Masterwork, 2025 Smuggler's Outpost, and 2025 Hyperspace. This one looks like a {readYear} card, so we can't add it here yet.</p>
              <p style={{ color: COLORS.muted, fontSize: 13, lineHeight: 1.5, marginTop: 8 }}>You can still add it by hand from its set page.</p>
            </div>
          ) : (
            <div style={{ background: COLORS.cardBg, padding: 16, borderRadius: 8, marginTop: 16 }}>
              <div style={{ fontFamily: 'Orbitron, sans-serif', color: COLORS.muted, fontSize: 14, letterSpacing: 1, marginBottom: 10 }}>COULD NOT IDENTIFY</div>
              <p style={{ color: '#fff', fontSize: 14, lineHeight: 1.5 }}>Nothing matched the catalog. Try clearer photos with the card number and title in frame, or add it by hand from the set.</p>
            </div>
          );
        })()}

        {dec && dec !== 'graded' && dec !== 'no_match' && (
          <div style={{ background: COLORS.cardBg, padding: 16, borderRadius: 8, marginTop: 16 }}>
            <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 14, letterSpacing: 1, marginBottom: 10, color: activeCard && dec === 'verified' ? COLORS.green : COLORS.amber }}>
              {activeCard && dec === 'verified' ? 'IDENTIFIED' : 'PLEASE CONFIRM'}
            </div>

            {!chosenCard && dec === 'needs_confirmation' && (result?.candidates || []).length > 0 && (
              <>
                <p style={{ color: COLORS.muted, fontSize: 13, marginBottom: 10, lineHeight: 1.5 }}>
                  {result?.card ? 'This looks closest — tap to confirm, or pick another below.' : 'This card appears in more than one place. Tap the one you are holding.'}
                </p>
                {(result.candidates).map((c, i) => (
                  <button key={i} onClick={() => setChosenCard(c)} style={{
                    display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', marginBottom: 8, borderRadius: 6,
                    border: `1px solid ${COLORS.amber}55`, background: 'var(--panel-2)', cursor: 'pointer',
                  }}>
                    <div style={{ color: COLORS.amber, fontSize: 13, fontWeight: 600 }}>{c.set_name}</div>
                    <div style={{ color: '#fff', fontSize: 13 }}>{c.name} · #{c.card_number}{c.sub_set_type !== 'base' ? ` · ${c.sub_set_name}` : ''}</div>
                  </button>
                ))}
              </>
            )}

            {activeCard && (
              <>
                <Field label="SET" value={activeCard.set_name} state={chosenCard ? 'unverified' : result?.card_provenance} />
                <Field label="NUMBER" value={activeCard.card_number} state={chosenCard ? 'unverified' : result?.card_provenance} />
                <Field label="NAME" value={activeCard.name} state={chosenCard ? 'unverified' : result?.card_provenance} />
                {activeCard.sub_set_type !== 'base' && <Field label="SUB-SET" value={activeCard.sub_set_name} state={chosenCard ? 'unverified' : result?.card_provenance} />}

                {/* Parallel — always a constrained dropdown so the collector can set or change it */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 0', borderBottom: `1px solid ${COLORS.muted}22` }}>
                  <div style={{ color: COLORS.cyan, fontSize: 11, width: 88, letterSpacing: 0.5, flexShrink: 0, paddingTop: 8 }}>PARALLEL</div>
                  <div style={{ flex: 1 }}>
                    <select value={chosenParallelId} onChange={(e) => setChosenParallelId(e.target.value)} style={{
                      width: '100%', padding: '8px 9px', background: 'var(--panel-2)',
                      color: (chosenParallelId === BASE_OPT) ? '#fff' : (chosenParallelId === '' ? COLORS.amber : '#fff'),
                      border: `1px solid ${parallelPill === 'verified' ? COLORS.green : (parallelPill === 'empty' ? COLORS.muted : COLORS.amber)}`,
                      borderRadius: 4, fontFamily: 'Rajdhani, sans-serif', fontSize: 13,
                    }}>
                      {chosenParallelId === '' && <option value="">SELECT — TAP TO CONFIRM</option>}
                      <option value={BASE_OPT}>{mapped?.set?.basePrintRun ? `Base /${mapped.set.basePrintRun}` : 'Base — no parallel'}</option>
                      {matchParallels.length > 0 ? (
                        <>
                          <optgroup label="MATCHES YOUR SERIAL">
                            {matchParallels.map((p, i) => (
                              <option key={`m${i}`} value={p.id}>{p.name}{p.print_run ? ` /${p.print_run}` : ''}</option>
                            ))}
                          </optgroup>
                          <optgroup label="ALL PARALLELS">
                            {otherParallels.map((p, i) => (
                              <option key={`o${i}`} value={p.id}>{p.name}{p.print_run ? ` /${p.print_run}` : ''}</option>
                            ))}
                          </optgroup>
                        </>
                      ) : frontendParallels.map((p, i) => (
                        <option key={i} value={p.id}>{p.name}{p.print_run ? ` /${p.print_run}` : ''}</option>
                      ))}
                    </select>
                    {result?.flags?.includes('mini_diamond_texture_suggested') && (
                      <div style={{ color: COLORS.amber, fontSize: 11, marginTop: 5, lineHeight: 1.4 }}>Looks like a Mini-Diamond — confirm or change.</div>
                    )}
                    {result?.flags?.includes('foil_pattern_suggested') && !chosenCard && (
                      <div style={{ color: COLORS.amber, fontSize: 11, marginTop: 5, lineHeight: 1.4 }}>Picked from the foil pattern — confirm or change.</div>
                    )}
                    {result?.parallel_provenance === 'unverified' && !result?.parallel && (result?.parallel_candidates || []).length > 0 && (
                      <div style={{ color: COLORS.amber, fontSize: 11, marginTop: 5, lineHeight: 1.4 }}>
                        {sharedRun != null
                          ? `${matchParallels.length} parallels are numbered /${sharedRun} — choose yours.`
                          : "We couldn't verify the parallel from the serial — choose the right one."}
                      </div>
                    )}
                  </div>
                  <span style={{ paddingTop: 8 }}><Pill state={parallelPill} /></span>
                </div>

                <button onClick={addToChecklist} disabled={busy} style={primary(parallelUnconfirmed ? COLORS.amber : COLORS.green, true)}>
                  ADD TO MY CHECKLIST
                </button>
                {parallelUnconfirmed && (
                  <p style={{ color: COLORS.amber, fontSize: 12, marginTop: 8, lineHeight: 1.4 }}>Choose the parallel above to continue.</p>
                )}
              </>
            )}
          </div>
        )}

        {/* Only when the scan did NOT cleanly identify the card — on a clean
            VERIFIED match this just repeats the result above, so it's hidden.
            On a miss/confirm it helps the collector see what was read. */}
        {dec && dec !== 'graded' && !(activeCard && dec === 'verified') && (
          <details style={{ marginTop: 14 }}>
            <summary style={{ color: COLORS.muted, fontSize: 11, letterSpacing: 1, cursor: 'pointer' }}>WHAT THE SCAN READ</summary>
            <div style={{ marginTop: 8 }}>
              <Field label="NUMBER" value={ai.card_number} state="unverified" />
              <Field label="NAME" value={ai.name} state="unverified" />
              <Field label="YEAR" value={ai.year} state="unverified" />
              <Field label="SERIAL" value={ai.serial_denominator ? `${ai.serial_numerator ?? '?'}/${ai.serial_denominator}` : null} state="unverified" />
            </div>
          </details>
        )}

        {modal && (
          <CollectionModal
            isOpen={true}
            user={user}
            card={modal.card}
            setMeta={modal.setMeta}
            set={modal.set}
            mode="new"
            initialParallelId={modal.parallelId}
            initialSerial={ai.serial_numerator != null ? String(ai.serial_numerator) : ''}
            initialPhotoFront={scanPhotos?.front || null}
            initialPhotoBack={scanPhotos?.back || null}
            existingEntry={null}
            onSaved={() => { setModal(null); clearAll(); setStatus('Added to your checklist.'); }}
            onClose={() => setModal(null)}
          />
        )}
      </div>
    </div>
  );
}
